import { MinusIcon, PlusIcon } from 'lucide-react';

import type { KeyboardEvent } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

export interface SharesStepperProps {
  /** Clamped to min–max before it is shown or stepped. */
  value: number;
  /** 0 lets the last minus mean "none" (an item row's release). */
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  /** Defaults to the Shares labels. */
  labels?: { decrease: string; increase: string };
  /** Test ids read `${prefix}-minus`, `-value` and `-plus`, then `-${suffix}` when given. */
  testIdPrefix?: string;
  testIdSuffix?: string;
}

// A bound is aria-disabled, not disabled: a disabled button drops keyboard
// focus to the page, and the arrow keys would stop working.
const buttonClass =
  'group flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 aria-disabled:cursor-not-allowed disabled:cursor-not-allowed';
const discClass =
  'flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors group-hover:bg-gray-100 group-aria-disabled:border-gray-200 group-aria-disabled:opacity-40 group-aria-disabled:group-hover:bg-transparent group-disabled:border-gray-200 group-disabled:opacity-40 group-disabled:group-hover:bg-transparent motion-reduce:transition-none dark:border-gray-600 dark:text-gray-400 dark:group-hover:bg-gray-700 dark:group-aria-disabled:border-gray-700 dark:group-disabled:border-gray-700';

/** Minus / value / plus for whole counts, inert at its bounds; ArrowUp and ArrowDown step from either button. */
export function SharesStepper({
  value,
  min = 1,
  max = 100,
  onChange,
  disabled = false,
  labels,
  testIdPrefix,
  testIdSuffix,
}: SharesStepperProps) {
  const { t } = useTranslation();
  const safe = Math.min(max, Math.max(min, Math.round(value)));
  const canDecrease = !disabled && safe > min;
  const canIncrease = !disabled && safe < max;
  const testId = (part: 'minus' | 'value' | 'plus') =>
    testIdPrefix
      ? `${testIdPrefix}-${part}${testIdSuffix ? `-${testIdSuffix}` : ''}`
      : undefined;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (canIncrease) onChange(safe + 1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (canDecrease) onChange(safe - 1);
    }
  };

  return (
    <div
      data-testid="shares-stepper"
      className="inline-flex items-center"
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        onClick={() => canDecrease && onChange(safe - 1)}
        disabled={disabled}
        aria-disabled={!canDecrease || undefined}
        aria-label={labels?.decrease ?? t('mobile:a11y.decreaseShares')}
        data-testid={testId('minus')}
        className={buttonClass}
      >
        <span aria-hidden="true" className={discClass}>
          <MinusIcon className="h-3.5 w-3.5" />
        </span>
      </button>
      <span
        aria-live="polite"
        data-testid={testId('value')}
        className="min-w-7 text-center text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100"
      >
        {safe}
      </span>
      <button
        type="button"
        onClick={() => canIncrease && onChange(safe + 1)}
        disabled={disabled}
        aria-disabled={!canIncrease || undefined}
        aria-label={labels?.increase ?? t('mobile:a11y.increaseShares')}
        data-testid={testId('plus')}
        className={buttonClass}
      >
        <span aria-hidden="true" className={discClass}>
          <PlusIcon className="h-3.5 w-3.5" />
        </span>
      </button>
    </div>
  );
}

export default SharesStepper;
